import { Component, computed, inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { ModalResponseCodes } from '@src/suite/common/components/modal/modal.service';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';

import { TextFieldComponent } from '../../controls/text-field/text-field';

@Component({
    selector: 'st-ro-duplicate-slug',
    imports: [MatButton, MatDialogClose, TextFieldComponent],
    templateUrl: './st-ro-duplicate-slug.html',
    styleUrl: 'st-ro-duplicate-slug.scss',
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-ro-duplicate-slug-dialog',
    },
})
export class StRoDuplicateSlugComponent implements OnInit {
    private dialogRef = inject(MatDialogRef<StRoDuplicateSlugComponent>);
    data: {
        story: StorytellingStoryRecord;
        rundown: StorytellingRundownRecord;
    } = inject(MAT_DIALOG_DATA);
    private store = inject(Store);

    ModalResponseCodes = ModalResponseCodes;

    // store signals
    languageSettings = this.store.selectSignal(selectI18N)();

    // signals
    readonly initialTitle = signal('');
    readonly placeholder = signal('');
    readonly value = signal('');
    readonly story = signal<StorytellingStoryRecord>(null);
    readonly rundown = signal<StorytellingRundownRecord>(null);

    // computed
    readonly title = computed(() => this.languageSettings.PSTWebDuplicateSlugTitle);
    readonly isTitleValid = computed(() => this.value() !== this.initialTitle());
    readonly storyTitle = computed(() => `"${this.story()?.Title?.trim()}" ${this.languageSettings.PSTWebDuplicateSlugNameAlreadyExists}`);
    readonly optionOneDesc = computed(() => this.languageSettings.PSTWebDuplicateSlugOptionOneLabel);
    readonly optionTwoDesc = computed(() => this.languageSettings.PSTWebDuplicateSlugOptionTwoLabel);
    readonly inputLabel = computed(() => this.languageSettings.PSTWebDuplicateSlugInputLabel);

    ngOnInit(): void {
        this.initialize();
    }

    initialize(): void {
        this.story.set(this.data.story);
        this.rundown.set(this.data.rundown);

        this.initilizeTitle();
    }

    initilizeTitle(): void {
        const initialTitle = this.story()?.Title?.trim();
        this.initialTitle.set(initialTitle);

        if (!initialTitle) return;
        let newTitlePlaceholder = initialTitle;

        const titleSet = new Set<string>();
        this.rundown().children.forEach(child => {
            titleSet.add(child.Title?.trim());
        });

        // find number into initialTitle
        const number = newTitlePlaceholder.match(/\d+/);
        if (number && number[0]) {
            newTitlePlaceholder = `${newTitlePlaceholder.replace(number[0], (Number(number[0]) + 1).toString())}`;

            if (titleSet.has(newTitlePlaceholder)) {
                newTitlePlaceholder = this.findNewTitle(newTitlePlaceholder, number[0], titleSet, 0);
            }
        } else {
            newTitlePlaceholder = `${newTitlePlaceholder} (1)`;
        }
        this.placeholder.set(newTitlePlaceholder);
        this.value.set(newTitlePlaceholder);
    }

    findNewTitle(initialTitle: string, number: string, titleSet: Set<string>, depth: number): string {
        // depth is used to prevent infinite loop
        if (depth > 100) return initialTitle;
        if (titleSet.size === 0) return initialTitle;

        const newNumber = Number(number) + 1;
        const newTitle = `${initialTitle.replace(number, newNumber.toString())}`;

        // If the new title doesn't exist in the set, we found a unique one
        if (!titleSet.has(newTitle)) {
            return newTitle;
        }

        // If it exists, try the next number
        titleSet.delete(newTitle);
        return this.findNewTitle(initialTitle, newNumber.toString(), titleSet, depth + 1);
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    onConfirm(): void {
        // Handle confirmation logic
        this.dialogRef.close(true);
    }
}
